import { useEffect, useId, useRef, useState } from 'react';
import { Check, Mars, Search, SlidersHorizontal, Venus, VenusAndMars, X } from 'lucide-react';
import type { AccordOption, Brand } from '../types.js';

export function FinderFilters({ brands, accords, params, update, clear }: {
  brands: Brand[];
  accords: AccordOption[];
  params: URLSearchParams;
  update: (key: string, value: string) => void;
  clear: () => void;
}) {
  const selected = (params.get('brand') ?? '').split(',').filter(Boolean);
  const gender = params.get('gender') ?? '';
  const chosenAccords = (params.get('accord') ?? '').split(',').filter(Boolean);
  const count = selected.length + chosenAccords.length + Number(Boolean(gender));
  const [sheet, setSheet] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!sheet) return;
    const element = dialog.current!;
    element.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const desktop = window.matchMedia('(min-width: 701px)');
    const resize = () => { if (desktop.matches) setSheet(false); };
    desktop.addEventListener('change', resize);
    return () => {
      element.close();
      document.body.style.overflow = previous;
      desktop.removeEventListener('change', resize);
      opener.current?.focus();
    };
  }, [sheet]);
  const toggle = (slug: string) => update('brand', selected.includes(slug)
    ? selected.filter(value => value !== slug).join(',') : [...selected, slug].join(','));
  const toggleAccord = (key: string) => update('accord', chosenAccords.includes(key)
    ? chosenAccords.filter(value => value !== key).join(',') : [...chosenAccords, key].join(','));
  const controls = (mobile: boolean) => <>
    <GenderFilter value={gender} choose={value => update('gender', value)}/>
    <BrandFilter brands={brands} selected={selected} toggle={toggle} clear={() => update('brand', '')} inline={mobile}/>
    <AccordFilter accords={accords} selected={chosenAccords} toggle={toggleAccord} clear={() => update('accord', '')} inline={mobile}/>
  </>;
  const chips = [
    ...selected.map(slug => ({ key: 'brand-' + slug, label: brands.find(brand => brand.slug === slug)?.name ?? slug, remove: () => toggle(slug) })),
    ...chosenAccords.map(key => ({ key: 'accord-' + key, label: accords.find(accord => accord.key === key)?.label ?? key, remove: () => toggleAccord(key) })),
    ...(gender ? [{ key: 'gender', label: genders.find(([value]) => value === gender)?.[1] ?? gender, remove: () => update('gender', '') }] : []),
  ];
  return <div className="finder-refinements">
    <div className="finder-desktop-filters">{controls(false)}</div>
    <button className="finder-mobile-toggle" ref={opener} onClick={() => setSheet(true)} aria-haspopup="dialog"><SlidersHorizontal size={17} aria-hidden/>Filters{count > 0 ? ` (${count})` : ''}</button>
    {count > 0 && <div className="finder-applied" aria-label="Active filters">
      {chips.map(chip => <button key={chip.key} className="finder-chip" onClick={chip.remove} aria-label={'Remove ' + chip.label + ' filter'}>{chip.label}<X size={14} aria-hidden/></button>)}
      <button className="finder-clear" onClick={clear}>Clear filters</button>
    </div>}
    {sheet && <dialog className="finder-sheet" ref={dialog} aria-labelledby="finder-sheet-title" onCancel={() => setSheet(false)} onClick={event => { if (event.target === event.currentTarget) setSheet(false); }}>
      <div className="finder-sheet-inner"><header><h2 id="finder-sheet-title">Filter fragrances</h2><button aria-label="Close filters" onClick={() => setSheet(false)}><X size={22}/></button></header>
        <p className="finder-sheet-hint">Results update as you choose.</p>
        <div className="finder-sheet-controls">{controls(true)}</div>
        <footer><button className="finder-clear" disabled={!count} onClick={clear}>Clear filters</button><button className="button" onClick={() => setSheet(false)}>Show matching fragrances</button></footer>
      </div>
    </dialog>}
  </div>;
}

const genders = [['', 'All genders'], ['male', 'Male'], ['female', 'Female'], ['unisex', 'Unisex']];

const genderIcons: Record<string, typeof Mars> = { male: Mars, female: Venus, unisex: VenusAndMars };

function GenderFilter({ value, choose }: { value: string; choose: (value: string) => void }) {
  const name = useId();
  return <fieldset className="finder-type finder-gender"><legend>Gender</legend>
    <div>{genders.map(([option, label]) => {
      const Icon = genderIcons[option];
      return <label key={option}><input type="radio" name={name} checked={value === option} onChange={() => choose(option)}/>
        <span>{Icon && <Icon size={15} aria-hidden/>}{option ? label : 'Any'}</span></label>;
    })}</div>
  </fieldset>;
}

type FilterProps = { selected: string[]; toggle: (value: string) => void; clear: () => void; inline: boolean };

function BrandFilter({ brands, ...props }: FilterProps & { brands: Brand[] }) {
  return <ChecklistFilter title="Brand" noun="brands" placeholder="Brand name or initials" {...props}
    options={brands.map(brand => ({ value: brand.slug, label: brand.name }))} sort={(a, b) => a.label.localeCompare(b.label)}/>;
}

// Accords keep the catalogue's order, most common first, so the likeliest choices lead the list.
function AccordFilter({ accords, ...props }: FilterProps & { accords: AccordOption[] }) {
  return <ChecklistFilter title="Accords" noun="accords" placeholder="Vanilla, woody, citrus…" {...props} align="end"
    hint="Every chosen accord is noticeable in the results"
    options={accords.map(accord => ({ value: accord.key, label: accord.label }))}/>;
}

function ClearAll(props: { onClick: () => void; onMouseDown?: (event: { preventDefault: () => void }) => void }) {
  return <button type="button" className="finder-clear-all" {...props}><X size={12} strokeWidth={2.25} aria-hidden/>Clear all</button>;
}

type ChecklistOption = { value: string; label: string };
const normalize = (value: string) => value.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
// Matches whole names, names without punctuation, or initials (e.g. "mfk", "ysl").
const matches = (label: string, query: string) => {
  const name = normalize(label), search = normalize(query.trim());
  return name.includes(search) || name.replace(/[^a-z0-9]/g, '').includes(search.replace(/[^a-z0-9]/g, '')) || name.split(/[^a-z0-9]+/).map(word => word[0] ?? '').join('').includes(search);
};

/** A type-to-search, multi-select field on desktop (like the country field) and an inline list in the mobile sheet. */
function ChecklistFilter({ title, noun, placeholder, hint, options, selected, toggle, clear, inline, align, sort }: FilterProps & {
  title: string; noun: string; placeholder: string; hint?: string; options: ChecklistOption[];
  align?: 'end'; sort?: (a: ChecklistOption, b: ChecklistOption) => number;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Chosen options rise to the top when the list opens, then hold still while the visitor ticks more.
  const [pinned, setPinned] = useState<string[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const lead = inline ? selected : pinned;
  const visible = options.filter(option => matches(option.label, query))
    .sort((a, b) => Number(lead.includes(b.value)) - Number(lead.includes(a.value)) || (sort ? sort(a, b) : 0));
  const status = selected.length ? `${selected.length} selected · ${hint ? hint.charAt(0).toLowerCase() + hint.slice(1) : 'choose any that apply'}` : hint ?? `Choose one or more ${noun}`;
  const labels = selected.map(value => options.find(option => option.value === value)?.label ?? value);
  const summary = labels.length ? labels[0] + (labels.length > 1 ? ` +${labels.length - 1}` : '') : '';
  const empty = <p className="combobox-empty" role="status">{options.length ? <>No {noun} match “{query}”.</> : `No ${noun} to choose from yet.`}</p>;

  useEffect(() => {
    if (open) document.getElementById(id + '-option-' + active)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);

  if (inline) return <section className="finder-brand-inline">
    <div className="finder-inline-head"><h3>{title}</h3>{selected.length > 0 && <ClearAll onClick={clear}/>}</div>
    <div className="finder-brand-content">
      <label htmlFor={id}>Search {noun}</label><input id={id} type="search" autoComplete="off" placeholder={placeholder} value={query} onChange={event => setQuery(event.target.value)}/>
      <p>{status}</p>
      <div className="finder-brand-options">{visible.map(option => <label key={option.value}><input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)}/><span>{option.label}</span></label>)}
        {!visible.length && empty}
      </div>
    </div>
  </section>;

  const show = () => {
    if (open) return;
    setPinned(selected);
    setQuery('');
    setActive(0);
    setOpen(true);
  };
  const close = () => { setOpen(false); setQuery(''); };
  const clearAll = () => { clear(); setPinned([]); input.current?.focus(); };
  const keepFocus = (event: { preventDefault: () => void }) => event.preventDefault();
  return <div className={'finder-combobox' + (align === 'end' ? ' is-end' : '')} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) close(); }}>
    <div className={'finder-combobox-field' + (open ? ' is-open' : '') + (selected.length ? ' has-value' : '')} onMouseDown={event => { if (event.target !== input.current) { event.preventDefault(); input.current?.focus(); show(); } }}>
      <input ref={input} role="combobox" aria-label={title + (labels.length ? ': ' + labels.join(', ') : '')} aria-expanded={open} aria-controls={id + '-list'}
        aria-autocomplete="list" aria-activedescendant={open && visible[active] ? id + '-option-' + active : undefined}
        autoComplete="off" spellCheck={false} placeholder={open ? 'Search ' + noun : title} value={open ? query : summary}
        onFocus={show} onClick={show}
        onChange={event => { if (!open) show(); setQuery(event.target.value); setActive(0); }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) return show();
            setActive(index => Math.max(0, Math.min(visible.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
          } else if (event.key === 'Enter' && open && visible[active]) {
            event.preventDefault();
            toggle(visible[active].value);
          } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            close();
          }
        }}/>
      {open && query
        ? <button type="button" className="finder-combobox-clear" aria-label={'Clear ' + title.toLowerCase() + ' search'} onMouseDown={keepFocus} onClick={() => { setQuery(''); setActive(0); input.current?.focus(); }}><X size={15} aria-hidden/></button>
        : selected.length > 0
          ? <button type="button" className="finder-combobox-clear" aria-label={'Clear all ' + noun} title={'Clear all ' + noun} onMouseDown={keepFocus} onClick={clearAll}><X size={15} aria-hidden/></button>
          : <Search className="finder-combobox-icon" size={15} aria-hidden/>}
    </div>
    {open && <div className="combobox-popover finder-combobox-popover">
      <div className="finder-combobox-status"><span>{selected.length ? <b>{selected.length} selected</b> : status}</span>{selected.length > 0 && <ClearAll onMouseDown={keepFocus} onClick={clearAll}/>}</div>
      <div className="combobox-list" role="listbox" aria-multiselectable="true" aria-label={title} id={id + '-list'}>
        {visible.map((option, index) => {
          const checked = selected.includes(option.value);
          return <div role="option" id={id + '-option-' + index} key={option.value} aria-selected={checked}
            className={'combobox-option' + (index === active ? ' is-active' : '')}
            onPointerMove={() => setActive(index)} onMouseDown={keepFocus} onClick={() => { setActive(index); toggle(option.value); }}>
            <span className="combobox-option-name">{option.label}</span>
            {checked && <Check size={16} aria-hidden/>}
          </div>;
        })}
      </div>
      {!visible.length && empty}
    </div>}
  </div>;
}
